import { getAuthToken } from "@lib/graphql/getAuthToken";
import Cookies from "js-cookie";
import { extractProfilesFromJWT as extractProfilesFromJWTPayload } from "./jwtPayload";

/**
 * Store the JWT token and expiration in cookies
 */
export function setAuthToken(token: string): void {
  // Match Rails API: cookie expires in 7 hours (JWT expires in 7 hours for nAPI)
  const expirationDate = new Date();
  expirationDate.setHours(expirationDate.getHours() + 7);

  const cookieOptions = {
    expires: expirationDate,
    sameSite: "strict" as const,
    secure: process.env.NODE_ENV === "production",
  };

  // Store the JWT token
  Cookies.set(`be_${window.siteId}`, token, cookieOptions);

  // Store the expiration date for client-side session checks
  Cookies.set(`be_${window.siteId}_e`, expirationDate.toISOString(), cookieOptions);
}

/**
 * Decode JWT and extract profile information
 */
export function extractProfilesFromJWT(token: string): be.Profiles | null {
  return extractProfilesFromJWTPayload(token);
}

/**
 * Get profiles directly from the JWT cookie.
 * This is the canonical source of truth for profiles, avoiding multi-tab desync issues
 * where sessionStorage (per-tab) may not match the JWT cookie (shared across tabs).
 */
export function getProfiles(): be.Profiles {
  const token = getAuthToken();
  if (!token) return {};
  return extractProfilesFromJWTPayload(token) || {};
}

/**
 * Process a JWT token: store it and reset Apollo cache.
 * Profiles are now read directly from JWT via getProfiles() - no session sync needed.
 */
export async function processJWT(token: string): Promise<be.Profiles | null> {
  setAuthToken(token);
  await window.apolloClient.resetStore();
  return extractProfilesFromJWTPayload(token);
}
